import React from "react";
import {
  Button,
  Paper,
  Typography,
  Box,
  IconButton,
  Tooltip,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { MatchPlayer } from "../types/firestoreTypes";

interface WaitingRoomProps {
  roomCode: string;
  players: MatchPlayer[];
  isHost: boolean;
  canStart: boolean;
  onBack: () => void;
  onAddBot: () => void;
  onRemoveBot: (playerId: string) => void;
  onStart: () => void;
}

const SEAT_LABELS = ["North", "East", "South", "West"];

export const WaitingRoom: React.FC<WaitingRoomProps> = ({
  roomCode,
  players,
  isHost,
  canStart,
  onBack,
  onAddBot,
  onRemoveBot,
  onStart,
}) => {
  const [copied, setCopied] = React.useState(false);

  const copyRoomCode = async () => {
    await navigator.clipboard.writeText(roomCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Create a 4-slot array with players in their seats
  const seats = [0, 1, 2, 3].map((seatIndex) => {
    return players.find((p) => p.seat === seatIndex) || null;
  });

  const humanCount = players.filter((p) => p.human).length;
  const totalPlayers = players.length;

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-b from-emerald-900 to-emerald-950 p-4">
      <Paper elevation={6} className="p-8 max-w-lg w-full rounded-xl">
        {/* Header */}
        <div className="flex items-center mb-6">
          <IconButton onClick={onBack} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" className="flex-1">
            Waiting Room
          </Typography>
        </div>

        {/* Room Code Display */}
        <Paper
          elevation={0}
          sx={{ backgroundColor: "#f3f4f6", mb: 4, p: 2 }}
          className="rounded-lg"
        >
          <Typography
            variant="body2"
            color="textSecondary"
            className="text-center"
          >
            Share this code with friends:
          </Typography>
          <Box className="flex items-center justify-center gap-2 mt-2">
            <Typography
              variant="h3"
              sx={{
                fontFamily: "monospace",
                letterSpacing: "0.3em",
                fontWeight: "bold",
              }}
            >
              {roomCode}
            </Typography>
            <Tooltip title={copied ? "Copied!" : "Copy code"}>
              <IconButton onClick={copyRoomCode} color={copied ? "success" : "default"}>
                {copied ? <CheckCircleIcon /> : <ContentCopyIcon />}
              </IconButton>
            </Tooltip>
          </Box>
        </Paper>

        {/* Players Table */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          {seats.map((player, index) => (
            <Paper
              key={index}
              elevation={1}
              sx={{
                p: 2,
                backgroundColor: player ? "#ecfdf5" : "#f9fafb",
                border: player ? "2px solid #10b981" : "2px dashed #d1d5db",
              }}
              className="rounded-lg flex items-center gap-3"
            >
              {player ? (
                <>
                  {player.human ? (
                    <PersonIcon sx={{ color: "#059669", fontSize: 32 }} />
                  ) : (
                    <SmartToyIcon sx={{ color: "#6b7280", fontSize: 32 }} />
                  )}
                  <div className="flex-1">
                    <Typography variant="body1" fontWeight="bold">
                      {player.name}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {SEAT_LABELS[index]} {player.human ? "" : "(Bot)"}
                    </Typography>
                  </div>
                  {!player.human && isHost && (
                    <IconButton
                      size="small"
                      onClick={() => onRemoveBot(player.id)}
                      sx={{ opacity: 0.5, "&:hover": { opacity: 1 } }}
                    >
                      ×
                    </IconButton>
                  )}
                </>
              ) : (
                <>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      backgroundColor: "#e5e7eb",
                    }}
                  />
                  <div className="flex-1">
                    <Typography variant="body1" color="textSecondary">
                      Waiting...
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {SEAT_LABELS[index]}
                    </Typography>
                  </div>
                </>
              )}
            </Paper>
          ))}
        </div>

        {/* Status */}
        <Typography
          variant="body2"
          color="textSecondary"
          className="text-center mb-4"
        >
          {humanCount} player{humanCount !== 1 ? "s" : ""} joined
          {totalPlayers < 4 && ` • Waiting for ${4 - totalPlayers} more`}
        </Typography>

        {/* Actions */}
        <div className="flex gap-2">
          {isHost && totalPlayers < 4 && (
            <Button
              variant="outlined"
              onClick={onAddBot}
              startIcon={<SmartToyIcon />}
              className="flex-1"
            >
              Add Bot
            </Button>
          )}
          {isHost && (
            <Button
              variant="contained"
              onClick={onStart}
              disabled={!canStart}
              className="flex-1"
              sx={{
                backgroundColor: "#059669",
                "&:hover": { backgroundColor: "#047857" },
              }}
            >
              Start Game
            </Button>
          )}
        </div>
      </Paper>
    </div>
  );
};
